import { HEADER_HEIGHT } from '@/constants'
import { trackPageView } from '@/utils/analytics'

type PageMeta = {
  title: string
  desc?: string
  scrollTo: string
  scrollBehavior: ScrollBehavior
  scrollDelay: number
  focusTitle: boolean
  analyticsTrack: boolean
}

type PageTitleDesc = {
  title: string
  desc?: string
}

/**
 * TODO: move to vue-router built in scroll behavior
 * Manually update page title and desc, focus and scroll to the top of the page
 * - for SPA routes, will focus #spa-page-title or fall back to #app
 * - will smooth scroll to top of page
 * @param title
 * @param desc
 * @param scrollTo
 * @param scrollBehavior
 * @param scrollDelay
 * @param focusTitle
 * @param analyticsTrack
 */
export const spaMetaUpdateAndTrack = ({
  title = '',
  desc,
  scrollTo = 'top',
  scrollBehavior = 'instant', // smooth and auto supported
  scrollDelay = 0, // used to wrap a setTimeout before scrolling
  focusTitle = true,
  analyticsTrack = false,
}: PageMeta) => {
  /* TITLE TAG UPDATES */
  updateMetaPageTitle({ title, desc })
  // end title

  /* SCROLL TO TOP - DEFAULT */
  // test for native smooth scroll options
  // eslint-disable-next-line
  const supportsNativeSmoothScroll = 'scrollBehavior' in document.documentElement.style

  if (scrollTo === 'top') {
    const scrollToTop = () => {
      if (supportsNativeSmoothScroll) {
        window.scrollTo({ top: 0, behavior: scrollBehavior })
      } else {
        window.scrollTo(0, 0)
      }
    }

    if (scrollDelay) {
      // if a delay is needed between routes for whatever reason before scrolling
      // pass in scrollDelay or manage scroll on the primary SPA page component
      // itself.
      //
      // could be useful if scrolling to SPA elem that has not rendered yet
      setTimeout(scrollToTop, scrollDelay)
    } else {
      scrollToTop()
    }
  }
  // end scroll to top

  // scroll to query selector element
  if (scrollTo && scrollTo !== 'top') {
    const scrollToElem = () => {
      const scrollEl = document.querySelector(`${scrollTo}`)

      if (scrollEl && scrollEl instanceof HTMLElement) {
        const scrollToY = scrollEl.offsetTop - HEADER_HEIGHT

        if (supportsNativeSmoothScroll) {
          window.scrollTo({ top: scrollToY, behavior: scrollBehavior })
        } else {
          window.scrollTo(0, scrollToY)
        }
      }
    }

    if (scrollDelay) {
      // if a delay is needed between routes for whatever reason before scrolling
      // pass in scrollDelay or manage scroll on the primary SPA page component
      // itself.
      //
      // could be useful if scrolling to SPA elem that has not rendered yet
      setTimeout(scrollToElem, scrollDelay)
    } else {
      scrollToElem()
    }
  }

  // focus primary title
  if (focusTitle) {
    // delayed focus to allow for rendering
    setTimeout(() => {
      const focusEl = document.querySelector('#spa-title')

      if (focusEl && focusEl instanceof HTMLElement) {
        // also focus, but not keyboard tab
        focusEl.style.outline = 'none'
        focusEl.setAttribute('tabindex', '-1')
        focusEl.focus()
      }
    }, 250)
  }

  // manually push to analytics
  // let XHRs settle before sending
  setTimeout(() => {
    if (analyticsTrack) {
      trackPageView()
    }
  }, 150)
}

/**
 * Allows for async page title updates
 */
export const updateMetaPageTitle = ({ title = '', desc }: PageTitleDesc) => {
  const titleEl = document.querySelector('head > title')

  if (title && titleEl) {
    titleEl.innerHTML = `${title} | Fitness Blender`
  }
}
